import { type MouseEvent, useState } from 'react'
import { useIntl } from 'react-intl'
import dynamic from 'next/dynamic'
import { useDialogState } from 'ariakit/dialog'
import type { DataViewStatus } from '@vtex/admin-ui'
import {
  csx,
  IconCode,
  IconCopySimple,
  IconCube,
  IconCurrencyCircleDollar,
  IconPencil,
  IconStorefront,
  IconWarningCircle,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  Skeleton,
  useMenuState,
} from '@vtex/admin-ui'
import { useAdmin } from '@vtex/raccoon-next'
import { FullPageSpinnerDialog } from '@/components/FullPageSpinner'
import {
  useNavigateToProductEditor,
  useProductEditorURL,
} from '@/components/products-listing/hooks/useEditorURLs'
import { useInactiveItemModal } from '@/components/products-listing/InactiveItemModal'
import { IndexedInfoModal } from '@/components/products-listing/IndexedInfoModal'
import { useProductSKUsOffersModal } from '@/components/products-listing/ProductSKUsOffersModal'
import { useAnalytics } from '@/contexts/analytics'
import { type ClickOnTheListEvent } from '@/contexts/analytics/customEvents'
import { useCopyProduct } from '@/hooks/useCopyProduct'
import { executeClickHandlers } from '@/lib/middleware'
import { adminNavigate, handleAnchorClickSafely } from '@/lib/navigate'
import { isProductActive } from '@/lib/productStatus'
import { generateFullSiteURL } from '@/lib/transform'
import { type Product } from '@/types/product'

import * as style from '../../products-listing.styles'

const CopyProductModal = dynamic(
  () =>
    import('@/components/CopyProductModal').then((mod) => mod.CopyProductModal),
  {
    ssr: false,
    loading: FullPageSpinnerDialog,
  }
)

export function ActionsCell(props: MenuCellProps) {
  const { item, context, isProductPage = false } = props
  const analytics = useAnalytics()

  const { formatMessage } = useIntl()

  const indexedInfoModalState = useDialogState()
  const inactiveItemModalState = useInactiveItemModal()
  const productSKUsOffersModal = useProductSKUsOffersModal()

  const copyProductModal = useDialogState()
  const { handleCopyProduct, isCopying } = useCopyProduct(item.id)
  const [shouldLoadCopyProductModal, setShouldLoadCopyProductModal] =
    useState(false)

  const state = useMenuState()
  const { account, workspace } = useAdmin()
  const siteUrl = `${generateFullSiteURL(account, workspace)}`

  const productLink = useProductEditorURL(item.id)
  const handleNavigateToProduct = useNavigateToProductEditor(item.id)

  const dispatchEventOnClick =
    (action: ClickOnTheListEvent['Action']) => () => {
      analytics.track('Clicked on the list', {
        Action: action,
      })
    }

  const viewInStoreLink = `https://${account}.myvtex.com/${item.storeUrl}/p`
  const isNewProduct = typeof item.id === 'string' || !item.id

  const handleIfProductIsActive =
    (callback: () => void) => (evt: MouseEvent<HTMLDivElement>) => {
      evt.stopPropagation()

      if (!isProductActive(item)) {
        return inactiveItemModalState.show()
      }

      callback()
    }

  const handleDuplicateProduct = async (evt: MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation()

    await handleCopyProduct()
    copyProductModal.hide()
  }

  const handleOpenCopyProductModal = () => {
    if (!shouldLoadCopyProductModal) {
      setShouldLoadCopyProductModal(true)
    }

    copyProductModal.setOpen(true)
  }

  const duplicateProductMenuItem = (
    <MenuItem
      label={formatMessage({
        id: 'admin/admin.catalog.copy-product-menu-label',
        defaultMessage: 'Duplicate',
      })}
      icon={<IconCopySimple />}
      onClick={executeClickHandlers(
        handleOpenCopyProductModal,
        dispatchEventOnClick('More options: duplicate product')
      )}
      disabled={isCopying}
    />
  )

  if (context === 'loading') {
    return <MenuCellSkeleton />
  }

  return (
    <>
      <div
        className={
          (csx(style.cellContainer),
          csx({
            width: isProductPage ? 'auto' : '100%',
            marginLeft: isProductPage ? '1rem' : '0',
          }))
        }
      >
        <MenuButton
          state={state}
          variant="neutralTertiary"
          onClick={(e) => e.stopPropagation()}
          labelHidden
        />
        <Menu
          state={state}
          aria-label="actions"
          className={csx(style.actionsMenu)}
        >
          {!isProductPage && (
            <>
              <a
                href={productLink}
                onAuxClick={dispatchEventOnClick('More options: edit product')}
                onClick={executeClickHandlers(
                  dispatchEventOnClick('More options: edit product'),
                  handleAnchorClickSafely(handleNavigateToProduct)
                )}
              >
                <MenuItem
                  label={formatMessage({
                    id: 'admin/admin.catalog.edit-product-menu-label',
                    defaultMessage: 'Edit product',
                  })}
                  icon={<IconPencil />}
                />
              </a>

              {duplicateProductMenuItem}
              <MenuDivider />
            </>
          )}
          {!isNewProduct && isProductPage && duplicateProductMenuItem}
          <a
            href={`${siteUrl}/admin/pricing/#/prices?productId=${item.id}&productName=${item.name}`}
            onAuxClick={dispatchEventOnClick('More options: edit prices')}
            onClick={executeClickHandlers(
              dispatchEventOnClick('More options: edit prices'),
              handleAnchorClickSafely(
                adminNavigate('pricing', '#/prices', {
                  productId: item.id,
                  productName: item.name,
                })
              )
            )}
          >
            <MenuItem
              label={formatMessage({
                id: 'admin/admin.catalog.edit-prices-menu-label',
                defaultMessage: 'Edit prices',
              })}
              icon={<IconCurrencyCircleDollar />}
            />
          </a>
          <a
            href={`${siteUrl}/admin/inventory?productId=${item.id}&keyword=${item.id}`}
            onAuxClick={dispatchEventOnClick('More options: edit inventory')}
            onClick={executeClickHandlers(
              dispatchEventOnClick('More options: edit inventory'),
              handleAnchorClickSafely(
                adminNavigate('inventory', '', {
                  productId: item.id,
                  keyword: item.id,
                })
              )
            )}
          >
            <MenuItem
              label={formatMessage({
                id: 'admin/admin.catalog.edit-inventory-menu-label',
                defaultMessage: 'Edit inventory',
              })}
              icon={<IconCube />}
            />
          </a>
          <MenuDivider />
          <a
            href={viewInStoreLink}
            target="_blank"
            onAuxClick={dispatchEventOnClick('More options: view in store')}
            onClick={executeClickHandlers(
              dispatchEventOnClick('More options: view in store'),
              handleAnchorClickSafely(() => {
                if (!item.storeUrl) {
                  inactiveItemModalState.show()

                  return
                }

                window.open(viewInStoreLink, '_blank')
              })
            )}
          >
            <MenuItem
              label={formatMessage({
                id: 'admin/admin.catalog.view-in-store-action-label',
                defaultMessage: 'View in store',
              })}
              icon={<IconStorefront />}
            />
          </a>

          <MenuItem
            label={formatMessage({
              id: 'admin/admin.catalog.price-and-inventory-tab-label',
              defaultMessage: 'Price and Inventory',
            })}
            icon={<IconWarningCircle />}
            onClick={executeClickHandlers(
              dispatchEventOnClick("More options: view offer's table"),
              handleIfProductIsActive(() => productSKUsOffersModal.show(item))
            )}
          />
          <MenuItem
            label={formatMessage({
              id: 'admin/admin.catalog.indexed-info-menu-label',
              defaultMessage: 'View indexed info',
            })}
            icon={<IconCode />}
            onClick={executeClickHandlers(
              dispatchEventOnClick('More options: view indexed info'),
              indexedInfoModalState.show
            )}
          />
        </Menu>
      </div>

      {/* !TODO add lazy loading */}
      <IndexedInfoModal state={indexedInfoModalState} item={item} />

      {shouldLoadCopyProductModal && (
        <CopyProductModal
          state={copyProductModal}
          productName={item.name}
          onConfirm={handleDuplicateProduct}
          isCopying={isCopying}
        />
      )}
    </>
  )
}

function MenuCellSkeleton() {
  return (
    <div className={csx(style.cellContainer)}>
      <Skeleton className={csx({ size: '2rem' })} />
    </div>
  )
}

interface MenuCellProps {
  item: Product
  context: DataViewStatus
  isProductPage?: boolean
}
